import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Loader from "../../../common/Loader";

const EditApplication = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    course: "",
    name: "",
    fatherName: "",
    motherName: "",
    dateOfBirth: "",
    gender: "",
    lastAcademicQualification: "",
    yearOfPassing: "",
    residentialPhone: "",
    mobileNumber: "",
    email: "",
    fullAddress: "",
    centreBranchName: "",
    centreCityName: "",
    heardAboutINSD: "",
    goalForCourse: "",
    parentGuardianName: "",
    parentPhoneNumber: "",
    parentAddress: "",
    parentOccupation: "",
    parentEmail: "",
    parentSupport: false,
  });

  useEffect(() => {
    fetchCourses();
    fetchApplicationData();
  }, [id]);

  const fetchCourses = async () => {
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/courses/get-all`,
        headers: {},
      };

      const response = await axios.request(config);
      if (response.data.success) {
        setCourses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to fetch courses");
    }
  };

  const fetchApplicationData = async () => {
    try {
      setFetchingData(true);
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/application/get-application-by-id/${id}`,
        headers: {},
      };
      const startTime = Date.now();

      const response = await axios.request(config);
      const applicationData = response.data.data;
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      // Format date for input field (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      setFormData({
        course: applicationData.course?._id || "",
        name: applicationData.name || "",
        fatherName: applicationData.fatherName || "",
        motherName: applicationData.motherName || "",
        dateOfBirth: formatDateForInput(applicationData.dateOfBirth) || "",
        gender: applicationData.gender || "",
        lastAcademicQualification:
          applicationData.lastAcademicQualification || "",
        yearOfPassing: applicationData.yearOfPassing || "",
        residentialPhone: applicationData.residentialPhone || "",
        mobileNumber: applicationData.mobileNumber || "",
        email: applicationData.email || "",
        fullAddress: applicationData.fullAddress || "",
        centreBranchName: applicationData.centreBranchName || "",
        centreCityName: applicationData.centreCityName || "",
        heardAboutINSD: applicationData.heardAboutINSD || "",
        goalForCourse: applicationData.goalForCourse || "",
        parentGuardianName: applicationData.parentGuardianName || "",
        parentPhoneNumber: applicationData.parentPhoneNumber || "",
        parentAddress: applicationData.parentAddress || "",
        parentOccupation: applicationData.parentOccupation || "",
        parentEmail: applicationData.parentEmail || "",
        parentSupport: applicationData.parentSupport || false,
      });

      setTimeout(() => setFetchingData(false), remaining);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to fetch application data.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
      navigate("/applications");
    } finally {
      setTimeout(() => setFetchingData(false), 1000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        yearOfPassing: parseInt(formData.yearOfPassing),
      };

      const config = {
        method: "put",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/application/edit-application/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(dataToSend),
      };

      const response = await axios.request(config);

      toast.success("Application updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      navigate("/applications");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update application. Please try again.",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return <Loader />;
  }

  return (
    <div className="w-full mx-auto rounded-lg shadow-md p-6">
      <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-4 mt-6">
        Edit Application
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Course Selection */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Course Information
          </h2>

          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              Select Course *
            </label>
            <select
              name="course"
              value={formData.course}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
            >
              <option value="">Select a course</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.name} - {course.category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Personal Information */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Personal Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Father's Name *
              </label>
              <input
                type="text"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Mother's Name *
              </label>
              <input
                type="text"
                name="motherName"
                value={formData.motherName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Date of Birth *
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Gender *
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              >
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                required
                pattern="[0-9]{10}"
                placeholder="10-digit mobile number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Residential Phone
              </label>
              <input
                type="tel"
                name="residentialPhone"
                value={formData.residentialPhone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              Full Address *
            </label>
            <textarea
              name="fullAddress"
              value={formData.fullAddress}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
            />
          </div>
        </div>

        {/* Academic Information */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Academic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Last Academic Qualification *
              </label>
              <input
                type="text"
                name="lastAcademicQualification"
                value={formData.lastAcademicQualification}
                onChange={handleInputChange}
                required
                placeholder="e.g., B.Sc in Computer Science"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Year of Passing *
              </label>
              <input
                type="number"
                name="yearOfPassing"
                value={formData.yearOfPassing}
                onChange={handleInputChange}
                required
                min="1950"
                max="2030"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
          </div>
        </div>

        {/* Centre Information */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Centre Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Centre Branch Name *
              </label>
              <input
                type="text"
                name="centreBranchName"
                value={formData.centreBranchName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Centre City Name *
              </label>
              <input
                type="text"
                name="centreCityName"
                value={formData.centreCityName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                How did you hear about INSD? *
              </label>
              <input
                type="text"
                name="heardAboutINSD"
                value={formData.heardAboutINSD}
                onChange={handleInputChange}
                required
                placeholder="e.g., Social Media, Friend, Website"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Goal for Course *
              </label>
              <input
                type="text"
                name="goalForCourse"
                value={formData.goalForCourse}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
          </div>
        </div>

        {/* Parent/Guardian Information */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-secondaryTextColor">
            Parent/Guardian Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Parent/Guardian Name *
              </label>
              <input
                type="text"
                name="parentGuardianName"
                value={formData.parentGuardianName}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Parent Phone Number *
              </label>
              <input
                type="tel"
                name="parentPhoneNumber"
                value={formData.parentPhoneNumber}
                onChange={handleInputChange}
                required
                pattern="[0-9]{10}"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Parent Email *
              </label>
              <input
                type="email"
                name="parentEmail"
                value={formData.parentEmail}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondaryTextColor mb-1">
                Parent Occupation *
              </label>
              <input
                type="text"
                name="parentOccupation"
                value={formData.parentOccupation}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondaryTextColor mb-1">
              Parent Address *
            </label>
            <textarea
              name="parentAddress"
              value={formData.parentAddress}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondaryColor"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="parentSupport"
              checked={formData.parentSupport}
              onChange={handleInputChange}
              className="w-4 h-4 text-secondaryColor border-gray-300 rounded focus:ring-secondaryColor"
            />
            <label className="ml-2 text-sm font-medium text-secondaryTextColor">
              Parent/Guardian supports this application
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/applications")}
            className="px-6 py-3 bg-gray-500 text-white font-medium rounded-md hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-md hover:bg-opacity-90 transition-opacity disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Updating Application..." : "Update Application"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditApplication;
